import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ShieldCheck,
  Copy,
  Check,
  AlertTriangle,
  Smartphone,
  Key,
  ArrowRight,
} from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionCard } from '@/components/common/SectionCard';
import { FormField } from '@/components/common/FormField';
import { ErrorState } from '@/components/common/ErrorState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { authService } from '@/services/auth';
import { session } from '@/lib/session';
import { ApiError } from '@/lib/errors';
import { homeForUser } from '@/config/navigation';
import type { MfaEnrollResult } from '@/types/auth';

type Step = 'enroll' | 'verify' | 'recovery';

/**
 * Two-factor authentication setup. Three steps:
 *  1. enroll  — fetch QR + secret from /auth/mfa/enroll
 *  2. verify  — operator types a TOTP code, call /auth/mfa/confirm
 *  3. recovery — show the one-time recovery codes (single display)
 */
export function MfaSetupPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('enroll');
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [secretCopied, setSecretCopied] = useState(false);
  const [recoveryCopied, setRecoveryCopied] = useState(false);

  // Fetch enrollment artefacts on mount.
  const enrollment = useQuery<MfaEnrollResult>({
    queryKey: ['auth', 'mfa', 'enroll'],
    queryFn: () => authService.mfaEnroll(),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const confirm = useMutation({
    mutationFn: () =>
      authService.mfaConfirm({
        enrollmentToken: enrollment.data!.enrollmentToken,
        code: code.trim(),
      }),
    onSuccess: async (result) => {
      setRecoveryCodes(result.recoveryCodes);
      setStep('recovery');
      // Refresh /auth/me so mfaEnrolled flips to true and the gate releases.
      try {
        const me = await authService.me();
        session.updateUser(me);
        qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      } catch {
        /* swallow — gate will recover next time */
      }
    },
  });

  // If enrollment errors with "already enrolled", route home.
  useEffect(() => {
    if (
      enrollment.error instanceof ApiError &&
      enrollment.error.status === 400 &&
      /already/i.test(enrollment.error.message)
    ) {
      const me = session.cachedUser();
      navigate(homeForUser(me), { replace: true });
    }
  }, [enrollment.error, navigate]);

  const onConfirm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    confirm.mutate();
  };

  const finish = () => {
    const me = session.cachedUser();
    navigate(homeForUser(me), { replace: true });
  };

  const copy = (text: string, setter: (v: boolean) => void) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setter(true);
        toast.success('Copied to clipboard');
        setTimeout(() => setter(false), 2000);
      })
      .catch(() => toast.error('Could not copy'));
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Security"
        title="Set up two-factor authentication"
        description="Required for admin-tier accounts. Adds a second factor to your sign-in via an authenticator app."
      />

      <ProgressIndicator step={step} />

      {/* ── Step 1: Enroll ─────────────────────────────────────────────── */}
      {step === 'enroll' && (
        <>
          {enrollment.error ? (
            <ErrorState
              error={enrollment.error}
              onRetry={() => enrollment.refetch()}
            />
          ) : enrollment.isLoading ? (
            <SectionCard title="Generating setup…">
              <div className="py-6 text-sm text-muted-foreground">
                Fetching your TOTP secret from the server.
              </div>
            </SectionCard>
          ) : (
            <SectionCard
              title={
                <span className="flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-muted-foreground" />
                  Scan with your authenticator app
                </span>
              }
              description="Use any TOTP app — 1Password, Authy, Google Authenticator, Microsoft Authenticator, Bitwarden."
            >
              <div className="grid grid-cols-1 gap-6 md:grid-cols-[200px_1fr]">
                {/* QR */}
                <div className="rounded-lg border border-border bg-white p-3">
                  {/* The runtime returns inline SVG markup. */}
                  <div
                    className="aspect-square w-full"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: enrollment.data!.qrSvg }}
                  />
                </div>
                <div className="space-y-4">
                  <p className="text-[13px] text-muted-foreground">
                    Open your authenticator app and scan the QR code on the left. If you can't
                    scan, enter the secret below manually.
                  </p>

                  <div className="space-y-1.5">
                    <div className="text-[12px] font-medium text-foreground">
                      Setup secret (enter manually if needed)
                    </div>
                    <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2.5 font-mono text-[12px]">
                      <span className="flex-1 break-all">{enrollment.data!.secret}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => copy(enrollment.data!.secret, setSecretCopied)}
                        className="-mr-2"
                      >
                        {secretCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-muted/30 px-3 py-2.5 text-[12px]">
                    <div className="font-medium text-foreground">Issuer / account label</div>
                    <div className="mt-1 text-muted-foreground">
                      The app will register this as <span className="font-mono">Zafroi</span> for{' '}
                      <span className="font-mono">{session.cachedUser()?.email}</span>.
                    </div>
                  </div>

                  <Button onClick={() => setStep('verify')} disabled={!enrollment.data}>
                    I've added it — continue
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </SectionCard>
          )}
        </>
      )}

      {/* ── Step 2: Verify ──────────────────────────────────────────────── */}
      {step === 'verify' && (
        <SectionCard
          title={
            <span className="flex items-center gap-2">
              <Key className="h-4 w-4 text-muted-foreground" />
              Verify your authenticator
            </span>
          }
          description="Enter the 6-digit code your app shows right now to confirm enrollment."
        >
          <form onSubmit={onConfirm} className="max-w-sm space-y-4">
            <FormField label="6-digit code" htmlFor="totp" required>
              <Input
                id="totp"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
                placeholder="123456"
                autoComplete="one-time-code"
                autoFocus
                className="h-11 text-center font-mono text-base tracking-[0.4em]"
                required
              />
            </FormField>

            {confirm.error && (
              <div
                role="alert"
                className="rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2.5 text-[12.5px] text-destructive"
              >
                <span className="font-semibold">
                  {confirm.error instanceof ApiError ? confirm.error.code : 'Error'}
                </span>
                <span className="ml-1">
                  {confirm.error instanceof Error ? confirm.error.message : 'Verification failed'}
                </span>
              </div>
            )}

            <div className="flex gap-2">
              <Button type="submit" disabled={confirm.isPending || code.length < 6} className="flex-1">
                {confirm.isPending ? 'Verifying…' : 'Confirm enrollment'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setStep('enroll')}>
                Back
              </Button>
            </div>
          </form>
        </SectionCard>
      )}

      {/* ── Step 3: Recovery codes ──────────────────────────────────────── */}
      {step === 'recovery' && (
        <SectionCard
          title={
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-success" />
              Save your recovery codes
            </span>
          }
          description="Single-use codes for when you don't have your authenticator."
        >
          <div className="space-y-4">
            <div className="rounded-md border border-warning/30 bg-warning/10 p-3">
              <div className="flex items-start gap-2 text-[12.5px] [color:hsl(38_85%_25%)]">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <div className="font-semibold">This is the only time these codes are shown.</div>
                  <div className="mt-0.5">
                    Store them somewhere safe — a password manager, a printed sheet, or a sealed
                    envelope. Each code is single-use.
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 rounded-md border bg-muted/40 p-4">
              {recoveryCodes.map((rc) => (
                <code
                  key={rc}
                  className="rounded-sm bg-card px-3 py-2 font-mono text-[13px] text-foreground"
                >
                  {rc}
                </code>
              ))}
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => copy(recoveryCodes.join('\n'), setRecoveryCopied)}
                className="flex-1"
              >
                {recoveryCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {recoveryCopied ? 'Codes copied' : 'Copy all codes'}
              </Button>
              <Button type="button" onClick={finish} className="flex-1">
                I've saved them — continue
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </SectionCard>
      )}
    </div>
  );
}

function ProgressIndicator({ step }: { step: Step }) {
  const steps: { key: Step; label: string }[] = [
    { key: 'enroll', label: 'Add to authenticator' },
    { key: 'verify', label: 'Verify' },
    { key: 'recovery', label: 'Save recovery codes' },
  ];
  const idx = steps.findIndex((s) => s.key === step);
  return (
    <ol className="flex flex-wrap items-center gap-x-3 gap-y-2">
      {steps.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <li key={s.key} className="flex items-center gap-2 text-[12.5px]">
            <Badge
              variant={active ? 'soft' : done ? 'success' : 'muted'}
              className="h-5 w-5 justify-center px-0 text-[10px]"
            >
              {done ? <Check className="h-3 w-3" /> : i + 1}
            </Badge>
            <span
              className={
                active
                  ? 'font-medium text-foreground'
                  : done
                    ? 'text-muted-foreground'
                    : 'text-muted-foreground/60'
              }
            >
              {s.label}
            </span>
            {i < steps.length - 1 && <span className="ml-1 text-muted-foreground/30">/</span>}
          </li>
        );
      })}
    </ol>
  );
}
